import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from 'shared/hooks/useAuth';

const STORAGE_PREFIX = 'dh.table.columnVisibility';

const readStored = (storageKey) => {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

const writeStored = (storageKey, model) => {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(model ?? {}));
  } catch {
    // Ignore quota / private-mode write failures.
  }
};

/**
 * Persist DataGrid columnVisibilityModel per table (+ tenant) in localStorage.
 * Survives reload; each table keeps its own selection via `tableId`.
 *
 * @param {string} tableId stable id, e.g. 'system-register.risk-register'
 */
export default function usePersistedColumnVisibility(tableId) {
  const auth = useAuth();
  const user = auth?.user;
  const tenantId = user?.data?.tenantId != null ? String(user.data.tenantId).trim() : '';

  const storageKey = useMemo(() => {
    const id = String(tableId || 'default').trim() || 'default';
    return tenantId ? `${STORAGE_PREFIX}.${tenantId}.${id}` : `${STORAGE_PREFIX}.${id}`;
  }, [tableId, tenantId]);

  const [columnVisibilityModel, setColumnVisibilityModel] = useState(() =>
    readStored(storageKey)
  );

  useEffect(() => {
    setColumnVisibilityModel(readStored(storageKey));
  }, [storageKey]);

  const onColumnVisibilityModelChange = useCallback(
    (nextModel) => {
      const safeModel = nextModel && typeof nextModel === 'object' ? nextModel : {};
      setColumnVisibilityModel(safeModel);
      writeStored(storageKey, safeModel);
    },
    [storageKey]
  );

  return { columnVisibilityModel, onColumnVisibilityModelChange };
}
